import React from "react";
import {
    ArrowUpRight,
    BadgeCheck,
    Check,
    Star,
} from "lucide-react";
import useReveal from "../lib/useReveal";

// =============================================================
//  Millionaire Partner System — /partner-system
//  v2.1 — Final visual polish (Keystone image-first pattern).
//  Layout only: image communicates first, text supports below.
//  No copy · no image · no color · no typography · no CTA
//  · no animation · no affiliate URL changes.
// =============================================================

const AFFILIATE_URL =
    "https://millionairepartnership.com/webclass-d24#aff=CrLuys-LifeStyle&cam=CAMPAIGNKEY";

const IMG = {
    hero: "/lifestyle/hero-penthouse.webp",
    community: "/lifestyle/community-mastermind.webp",
    office: "/lifestyle/office-worker.webp",
    manual: "/lifestyle/manual-workers.webp",
    strategy: "/lifestyle/strategy-session.webp",
    family: "/lifestyle/family-beach.webp",
    lifestyle: "/lifestyle/family-ocean-lunch.webp",
    mpsLogo: "/lifestyle/mps-logo.webp",
    mpsLogoTransparent: "/lifestyle/mps-logo-transparent.webp",
    guaranteeBadge: "/lifestyle/testimonials/guarantee-badge.png",
    glynnKosky: "/lifestyle/glynn-kosky.webp",
    testimonial1: "/lifestyle/testimonials/henry.webp",
    testimonial2: "/lifestyle/testimonials/mark.webp",
    testimonial3: "/lifestyle/testimonials/david.webp",
};

// ---------- Reusable atoms ----------

function CtaButton({ label = "Access the Free Masterclass", size = "md", testId }) {
    const sizes = {
        md: "px-8 py-4 text-sm tracking-[0.3em]",
        lg: "px-10 md:px-12 py-5 md:py-6 text-sm md:text-[0.95rem] tracking-[0.32em]",
    };
    return (
        <a
            href={AFFILIATE_URL}
            target="_blank"
            rel="noopener noreferrer sponsored"
            aria-label="Access the free Millionaire Partner System Masterclass"
            data-testid={testId || "partner-system-cta"}
            className={`group relative inline-flex items-center justify-center gap-3 uppercase font-medium rounded-full bg-gold text-ink hover:bg-gold/90 transition-all duration-300 shadow-[0_8px_30px_-8px_rgba(212,175,55,0.55)] hover:shadow-[0_10px_42px_-8px_rgba(212,175,55,0.75)] ${sizes[size]}`}
        >
            <span>{label}</span>
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
    );
}

function Eyebrow({ children, className = "" }) {
    return (
        <span
            className={`inline-block text-[0.7rem] uppercase tracking-[0.4em] text-gold/85 ${className}`}
        >
            {children}
        </span>
    );
}

// =============================================================
//  §1  HERO — Aspiration Solo (Pillar 3)
//  Keystone hero pattern: subject stays visible on the RIGHT;
//  MPS logo (upper-center) and entrepreneur (right) fully visible.
//  Text sits in the LEFT dark area (bookshelves negative space).
// =============================================================
function Hero() {
    return (
        <section
            data-testid="partner-system-hero"
            className="relative isolate overflow-hidden min-h-[100vh] md:min-h-[92vh] flex items-center"
        >
            <div className="absolute inset-0 -z-10">
                <img
                    src={IMG.hero}
                    alt="Entrepreneur working at a penthouse office desk beneath the Millionaire Partner System logo, overlooking a city skyline at sunset"
                    className="h-full w-full object-cover object-[68%_60%] md:object-center -translate-y-6 md:translate-y-4"
                    fetchPriority="high"
                />
                {/* MOBILE — layered mask:
                    (a) Top ~28% band: solid ink at top, backdrop-blur to smear any baked-in wall text
                        into indistinct texture, gradient fade to reveal businessman below.
                        Reduced from 38% to 28% so businessman's head stays fully clear on shorter phones (iPhone SE).
                    (b) Bottom ~34% band: fade to solid ink to give the headline + CTA a clean base
                        completely below the businessman + desk.
                    (c) Subtle global tint for atmospheric consistency. */}
                <div className="absolute inset-x-0 top-0 h-[28%] md:hidden backdrop-blur-md bg-gradient-to-b from-ink from-55% via-ink/80 via-80% to-transparent" />
                <div className="absolute inset-x-0 bottom-0 h-[34%] md:hidden bg-gradient-to-t from-ink from-40% via-ink/78 to-transparent" />
                <div className="absolute inset-0 md:hidden bg-ink/15" />
                {/* Desktop overlay: unchanged — left-anchored to keep the businessman fully visible. */}
                <div className="absolute inset-0 hidden md:block bg-gradient-to-r from-ink/78 via-ink/10 to-transparent" />
                {/* Bottom fade for section transition */}
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ink to-transparent" />
                <div className="absolute inset-0 bg-noise opacity-15" />
            </div>

            {/* ============ MOBILE HERO (< md) ============
                Composition:
                  · Top:    Official MPS logo (transparent via mix-blend-screen — the source
                            logo has a baked-in black background; `screen` blend renders the
                            black pixels invisible against the dark mask underneath, leaving
                            only the gold M chevron and white wordmark visible).
                  · Middle: Businessman, laptop, and skyline — fully unobstructed.
                  · Bottom: One short headline + gold CTA, positioned below the businessman
                            + desk so nothing overlaps the main photo. */}
            <div className="md:hidden absolute inset-0 flex flex-col items-center justify-between px-6 pt-16 pb-10 text-center z-10">
                {/* Top: Official Millionaire Partner System logo (true transparent PNG/WebP) */}
                <div
                    data-testid="partner-system-hero-mps-logo"
                    className="w-[300px] max-w-[82%]"
                >
                    <img
                        src={IMG.mpsLogoTransparent}
                        alt="Millionaire Partner System official logo"
                        fetchPriority="high"
                        className="w-full h-auto"
                    />
                </div>

                {/* Bottom: short headline + CTA — completely below the businessman */}
                <div className="w-full max-w-sm">
                    <h1
                        data-testid="partner-system-hero-headline"
                        className="font-display text-[1.75rem] leading-[1.15] text-cream"
                    >
                        Build a business through strategic{" "}
                        <span className="text-gold italic">partnerships.</span>
                    </h1>
                    <p
                        data-testid="partner-system-hero-confidence"
                        className="mt-5 mx-auto max-w-[19rem] text-[0.72rem] leading-relaxed text-cream/65"
                    >
                        No experience required. No product creation. Learn the exact
                        system used to build a scalable online business.
                    </p>
                    <div className="mt-6 flex flex-col items-center gap-4">
                        <CtaButton size="lg" testId="partner-system-hero-cta" />
                        <span className="text-[0.65rem] uppercase tracking-[0.35em] text-cream/55">
                            No commitment required
                        </span>
                    </div>
                </div>
            </div>

            {/* ============ DESKTOP HERO (>= md) — UNCHANGED ============ */}
            <div className="hidden md:block w-full max-w-full px-10 lg:px-16 py-32">
                <div className="max-w-md lg:max-w-lg mt-24">
                    <Eyebrow>Millionaire Partner System</Eyebrow>
                    <h1
                        data-testid="partner-system-hero-headline-desktop"
                        className="font-display mt-6 text-4xl lg:text-[2.6rem] leading-[1.15] text-cream"
                    >
                        Build a Business Through Strategic{" "}
                        <span className="text-gold italic">Partnerships.</span>
                    </h1>
                    <p className="mt-8 max-w-md text-base text-cream/75 leading-relaxed">
                        Discover the proven system designed to help entrepreneurs
                        leverage strategic partnerships, proven business models, and
                        scalable income opportunities to build greater freedom and
                        long-term wealth.
                    </p>
                    <p
                        data-testid="partner-system-hero-confidence-desktop"
                        className="mt-6 max-w-md text-xs text-cream/60 leading-relaxed"
                    >
                        No experience required. No product creation. Learn the exact
                        system used to build a scalable online business.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center gap-5">
                        <CtaButton size="lg" testId="partner-system-hero-cta-desktop" />
                        <span className="text-[0.65rem] uppercase tracking-[0.35em] text-cream/45">
                            No commitment required
                        </span>
                    </div>
                </div>
            </div>

            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/35 to-transparent" />
        </section>
    );
}

// =============================================================
//  §1.5  HOW IT WORKS — Immediately below Hero.
//  Reinforces the unique mechanism ("The Millionaire Partner Method™")
//  and communicates the 5 pillars of the program with premium gold
//  checkmarks. Matches the existing dark-luxury aesthetic — no new
//  images, no new fonts, no layout changes elsewhere.
// =============================================================
const HOW_IT_WORKS_POINTS = [
    "Learn the Partnership Framework",
    "Follow a Proven Business Model",
    "Leverage Mentorship and Community",
    "Build Scalable Income Opportunities",
    "Create Greater Time and Financial Freedom",
];

function HowItWorks() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-how-it-works"
            className="reveal relative py-24 md:py-32 bg-ink-100"
        >
            {/* Subtle ambient depth (mirrors WebClass + Guarantee sections) */}
            <div className="absolute inset-0 -z-10">
                <div
                    aria-hidden
                    className="absolute -top-40 -left-40 h-[32rem] w-[32rem] rounded-full bg-gold/6 blur-[130px]"
                />
                <div
                    aria-hidden
                    className="absolute -bottom-40 -right-40 h-[32rem] w-[32rem] rounded-full bg-gold/6 blur-[130px]"
                />
                <div className="absolute inset-0 bg-noise opacity-20" />
            </div>

            <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-12">
                <div className="text-center">
                    <Eyebrow>The Millionaire Partner Method&trade;</Eyebrow>
                    <h2
                        data-testid="partner-system-how-it-works-headline"
                        className="font-display mt-6 text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-cream"
                    >
                        How the{" "}
                        <span className="text-gold italic">
                            Millionaire Partner System
                        </span>{" "}
                        Works
                    </h2>
                    <p
                        data-testid="partner-system-how-it-works-intro"
                        className="mt-6 md:mt-7 max-w-2xl mx-auto text-sm md:text-base text-cream/70 leading-relaxed"
                    >
                        A proven business model that combines strategic partnerships,
                        mentorship, and scalable income systems &mdash; engineered to
                        help you build long-term financial freedom.
                    </p>
                </div>

                {/* 5 pillars */}
                <ul
                    data-testid="partner-system-how-it-works-list"
                    className="mt-14 md:mt-16 grid gap-4 md:gap-5 max-w-2xl mx-auto"
                >
                    {HOW_IT_WORKS_POINTS.map((point, i) => (
                        <li
                            key={point}
                            data-testid={`partner-system-how-it-works-item-${i}`}
                            className="group flex items-center gap-5 rounded-xl border border-gold/15 bg-ink/60 px-5 py-5 md:px-6 md:py-6 hover:border-gold/40 hover:bg-ink/80 transition-all duration-300"
                        >
                            <span className="flex-shrink-0 flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-full bg-gold/12 border border-gold/40">
                                <Check
                                    className="h-4 w-4 md:h-[1.1rem] md:w-[1.1rem] text-gold"
                                    strokeWidth={2.5}
                                />
                            </span>
                            <span className="text-[0.65rem] md:text-xs uppercase tracking-[0.35em] text-gold/70">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="font-display text-lg md:text-xl text-cream leading-tight">
                                {point}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
}

// =============================================================
//  §2  PRIVATE COMMUNITY — Community Night (Pillar 5)
//  Keystone image-first pattern: full-width image → text below.
// =============================================================
function PrivateCommunity() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-community"
            className="reveal relative py-24 md:py-32"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                {/* Official Millionaire Partner System logo — brand anchor */}
                <div
                    data-testid="partner-system-community-logo"
                    className="mx-auto mb-14 md:mb-16 w-[180px] md:w-[200px] lg:w-[210px]"
                >
                    <img
                        src={IMG.mpsLogo}
                        alt="Millionaire Partner System official logo"
                        loading="lazy"
                        className="w-full h-auto"
                    />
                </div>

                <figure className="relative overflow-hidden rounded-2xl border border-gold/15 aspect-[16/9] md:aspect-[21/9]">
                    <img
                        src={IMG.community}
                        alt="A private mastermind meeting inside a nighttime executive boardroom overlooking a city skyline"
                        loading="lazy"
                        className="h-full w-full object-cover object-center"
                    />
                </figure>

                <div className="mt-10 md:mt-12 max-w-2xl mx-auto text-center">
                    <Eyebrow>A Private Community</Eyebrow>
                    <h2
                        data-testid="partner-system-community-headline"
                        className="font-display mt-5 text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-cream"
                    >
                        A structured community for{" "}
                        <span className="text-gold italic">online business builders.</span>
                    </h2>
                    <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-lg mx-auto">
                        Training, mentorship, and peer discussion in one place.
                    </p>
                </div>
            </div>
        </section>
    );
}

// =============================================================
//  §4  OFFICE WORKER — Problem (Pillar 1)
//  Already Keystone-style: side-by-side (stacks on mobile).
// =============================================================
function OfficeWorker() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-office"
            className="reveal relative py-24 md:py-32 bg-ink-100"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-7">
                        <figure className="relative overflow-hidden rounded-2xl border border-gold/15 aspect-[4/3]">
                            <img
                                src={IMG.office}
                                alt="A professional at a modern office desk, hands on temples, with colleagues working in the background"
                                loading="lazy"
                                className="h-full w-full object-cover object-center"
                            />
                        </figure>
                    </div>

                    <div className="lg:col-span-5">
                        <Eyebrow>The Reality of Modern Work</Eyebrow>
                        <h2
                            data-testid="partner-system-office-headline"
                            className="font-display mt-5 text-3xl md:text-4xl leading-[1.1] text-cream"
                        >
                            You trade your most valuable asset:{" "}
                            <span className="text-gold italic">your time.</span>
                        </h2>
                        <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-md">
                            Long working hours become routine. Bills get paid, but time
                            with your family, personal freedom, and the life you truly
                            want keep getting pushed further away. Many people end up
                            repeating the same cycle year after year.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// =============================================================
//  §4  MANUAL WORKERS — Reality (Pillar 2)
//  Already Keystone-style: label + copy above, image below.
// =============================================================
function ManualWorkers() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-manual"
            className="reveal relative py-24 md:py-32"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <div className="max-w-2xl">
                    <Eyebrow>Every Profession Deserves Respect</Eyebrow>
                    <h2
                        data-testid="partner-system-manual-headline"
                        className="font-display mt-5 text-3xl md:text-4xl leading-[1.1] text-cream"
                    >
                        Every job trades{" "}
                        <span className="text-gold italic">your time</span>{" "}
                        for income.
                    </h2>
                    <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-lg">
                        Long working days often become a never-ending cycle. Bills get
                        paid, but quality time with your family, your health, and the
                        freedom to truly enjoy life keep slipping away. Building
                        additional income streams can create more choices and help you
                        reclaim control over your future.
                    </p>
                </div>

                <figure className="mt-12 md:mt-16 relative overflow-hidden rounded-2xl border border-gold/15 aspect-[4/3] md:aspect-[16/10] lg:aspect-[16/9]">
                    <img
                        src={IMG.manual}
                        alt="A four-part composition of workers in construction, industry, cleaning services, and window washing"
                        loading="lazy"
                        className="h-full w-full object-cover object-center"
                    />
                </figure>
            </div>
        </section>
    );
}

// =============================================================
//  §5  STRATEGY SESSION — Framework Day (Pillar 4)
//  Already Keystone-style: side-by-side (image right, text left).
// =============================================================
function StrategySession() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-strategy"
            className="reveal relative py-24 md:py-32 bg-ink-100"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
                    <div className="lg:col-span-7 lg:order-2">
                        <figure className="relative overflow-hidden rounded-2xl border border-gold/15 aspect-[16/10]">
                            <img
                                src={IMG.strategy}
                                alt="A daytime strategy session inside a corporate boardroom with an ascending performance chart on a monitor"
                                loading="lazy"
                                className="h-full w-full object-cover object-center"
                            />
                        </figure>
                    </div>

                    <div className="lg:col-span-5 lg:order-1">
                        <Eyebrow>How The Program Works</Eyebrow>
                        <h2
                            data-testid="partner-system-strategy-headline"
                            className="font-display mt-5 text-3xl md:text-4xl leading-[1.1] text-cream"
                        >
                            Professional Mentorship,{" "}
                            <span className="text-gold italic">that accelerates your journey.</span>
                        </h2>
                        <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-md">
                            Work alongside experienced mentors who provide practical
                            guidance, proven strategies, and the confidence to help you
                            build the financial future you&rsquo;ve been working toward.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// =============================================================
//  §6  FAMILY FREEDOM — Lifestyle Nature (Pillar 6a)
//  Keystone image-first pattern: full-width image → text below.
// =============================================================
function FamilyFreedom() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-family"
            className="reveal relative py-24 md:py-32"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <figure className="relative overflow-hidden rounded-2xl border border-gold/15 aspect-[16/9] md:aspect-[21/9]">
                    <img
                        src={IMG.family}
                        alt="A family sitting together on a tropical beach at midday, palm trees framing the composition"
                        loading="lazy"
                        className="h-full w-full object-cover object-center"
                    />
                </figure>

                <div className="mt-10 md:mt-12 max-w-2xl mx-auto text-center">
                    <Eyebrow>The Practical Outcome</Eyebrow>
                    <h2
                        data-testid="partner-system-family-headline"
                        className="font-display mt-5 text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-cream"
                    >
                        True wealth is measured in{" "}
                        <span className="text-gold italic">moments, not possessions.</span>
                    </h2>
                    <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-xl mx-auto">
                        Enjoy meaningful time with the people who matter most. Financial
                        flexibility gives you the freedom to create unforgettable
                        memories, travel together, celebrate life&rsquo;s milestones,
                        and simply be present for the moments that can never be
                        replaced.
                    </p>
                </div>
            </div>
        </section>
    );
}

// =============================================================
//  §7  LIFESTYLE — Lifestyle Social Gathering (Pillar 6b · Image 7)
//  Keystone image-first pattern: full-width image → text below.
// =============================================================
function Lifestyle() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-lifestyle"
            className="reveal relative py-24 md:py-32 bg-ink-100"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <figure className="relative overflow-hidden rounded-2xl border border-gold/15 aspect-[16/9] md:aspect-[21/9]">
                    <img
                        src={IMG.lifestyle}
                        alt="A family sharing a brunch at a linen-dressed table at the ocean's edge under a tropical canopy"
                        loading="lazy"
                        className="h-full w-full object-cover object-center"
                    />
                </figure>

                <div className="mt-10 md:mt-12 max-w-2xl mx-auto text-center">
                    <Eyebrow>A Wider Definition</Eyebrow>
                    <h2
                        data-testid="partner-system-lifestyle-headline"
                        className="font-display mt-5 text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-cream"
                    >
                        Some moments are simply{" "}
                        <span className="text-gold italic">priceless.</span>
                    </h2>
                    <p className="mt-6 text-sm md:text-base text-cream/70 leading-relaxed max-w-xl mx-auto">
                        Create unforgettable memories by investing your time where it
                        matters most. Success isn&rsquo;t only about earning more &mdash;
                        it&rsquo;s about having the freedom to enjoy life with the
                        people you love.
                    </p>
                </div>
            </div>
        </section>
    );
}

// =============================================================
//  §10  TESTIMONIALS — Official (photos, ★★★★★, verified badge)
//  Sanitized: income amounts + specific timeframes removed per
//  brand policy (results not typical). Voice preserved from
//  official source. Individual results may vary.
// =============================================================
const TESTIMONIALS = [
    {
        photo: IMG.testimonial1,
        quote: "When I stumbled across the Millionaire Partner System, I was skeptical because I had no experience with online income programs. The tools were incredibly easy to use, and the training showed me exactly what to do. I never imagined I could achieve this level of success, and I&rsquo;m just getting started.",
        name: "Henry T.",
        location: "Denver, CO",
    },
    {
        photo: IMG.testimonial2,
        quote: "As someone who always struggled to make money online, this system was a breath of fresh air. It&rsquo;s all thanks to the step-by-step guidance and automated tools. I didn&rsquo;t need to be an expert &mdash; the process was clear and accessible.",
        name: "Mark M.",
        location: "Denver, CO",
    },
    {
        photo: IMG.testimonial3,
        quote: "I had tried countless online programs with little to show for it, but the Millionaire Partner System delivered far beyond my expectations. The automation features took the heavy lifting off my plate, allowing me to focus on growing my business.",
        name: "David H.",
        location: "Miami, FL",
    },
];

function Testimonials() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-testimonials"
            className="reveal relative py-24 md:py-32"
        >
            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <div className="max-w-2xl mx-auto text-center">
                    <Eyebrow>In Their Words</Eyebrow>
                    <h2
                        data-testid="partner-system-testimonials-headline"
                        className="font-display mt-5 text-3xl md:text-4xl leading-[1.1] text-cream"
                    >
                        Members describe{" "}
                        <span className="text-gold italic">their experience.</span>
                    </h2>
                    <p
                        data-testid="partner-system-testimonials-intro"
                        className="mt-6 md:mt-7 text-sm md:text-base text-cream/70 leading-relaxed"
                    >
                        Thousands of entrepreneurs have already discovered the power
                        of strategic partnerships. Here&rsquo;s what they say.
                    </p>
                </div>

                <div className="mt-14 md:mt-16 grid md:grid-cols-3 gap-5 md:gap-6">
                    {TESTIMONIALS.map(({ photo, quote, name, location }, i) => (
                        <figure
                            key={name}
                            data-testid={`partner-system-testimonial-${i}`}
                            className="relative rounded-2xl border border-gold/15 bg-ink/60 p-7 md:p-8 hover:border-gold/40 hover:bg-ink transition-all duration-300 flex flex-col items-center text-center"
                        >
                            <img
                                src={photo}
                                alt={`${name} — verified customer`}
                                loading="lazy"
                                className="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-gold/30"
                            />
                            <div className="mt-5 flex items-center gap-3">
                                <div
                                    className="flex text-gold"
                                    role="img"
                                    aria-label="Five out of five stars"
                                    data-testid={`partner-system-testimonial-${i}-stars`}
                                >
                                    {[0, 1, 2, 3, 4].map((s) => (
                                        <Star
                                            key={s}
                                            className="h-3.5 w-3.5 fill-current"
                                            strokeWidth={0}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div className="mt-3 inline-flex items-center gap-1.5 text-[0.6rem] md:text-[0.65rem] uppercase tracking-[0.3em] text-cream/60">
                                <BadgeCheck
                                    className="h-3.5 w-3.5 text-gold"
                                    strokeWidth={2}
                                />
                                Verified Purchase
                            </div>
                            <blockquote
                                className="mt-6 text-sm md:text-[0.95rem] text-cream/85 leading-relaxed font-display italic"
                                dangerouslySetInnerHTML={{
                                    __html: `\u201C${quote}\u201D`,
                                }}
                            />
                            <figcaption className="mt-6 pt-5 border-t border-gold/15 w-full text-sm">
                                <span className="block text-cream/90 font-medium">
                                    {name}
                                </span>
                                <span className="block text-cream/50 text-[0.65rem] uppercase tracking-[0.28em] mt-1">
                                    {location}
                                </span>
                            </figcaption>
                        </figure>
                    ))}
                </div>

                <p
                    data-testid="partner-system-testimonials-disclaimer"
                    className="mt-12 text-center text-[0.7rem] text-cream/40 uppercase tracking-[0.2em]"
                >
                    Individual results may vary. Testimonials sourced from the official
                    Millionaire Partner System website.
                </p>
            </div>
        </section>
    );
}

// =============================================================
//  §11  OFFICIAL WEB CLASS — Presented by Glynn Kosky
//  All content sourced factually from the official
//  Millionaire Partner System page. No invented claims.
// =============================================================
const WEBCLASS_OUTCOMES = [
    "Build a solid foundation with professional websites, landing pages, and high-converting sales funnels.",
    "Identify profitable products and high-demand niches worth promoting.",
    "Craft impactful marketing campaigns using ready-made tools and templates.",
    "Leverage automation for email marketing, content distribution, and follow-ups.",
    "Track campaign performance with real-time data and analytics.",
    "Scale income potential with a proven, step-by-step framework.",
];

function WebClass() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-webclass"
            className="reveal relative py-24 md:py-32 overflow-hidden bg-ink-100"
        >
            {/* Subtle ambient depth */}
            <div className="absolute inset-0 -z-10">
                <div
                    aria-hidden
                    className="absolute -top-40 -left-40 h-[36rem] w-[36rem] rounded-full bg-gold/6 blur-[130px]"
                />
                <div
                    aria-hidden
                    className="absolute -bottom-40 -right-40 h-[36rem] w-[36rem] rounded-full bg-gold/6 blur-[130px]"
                />
                <div className="absolute inset-0 bg-noise opacity-20" />
            </div>

            <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-12">
                <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-center">
                    {/* Portrait — Glynn Kosky */}
                    <div className="lg:col-span-5">
                        <figure
                            data-testid="partner-system-webclass-portrait"
                            className="relative overflow-hidden rounded-2xl border border-gold/20 aspect-square shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]"
                        >
                            <img
                                src={IMG.glynnKosky}
                                alt="Glynn Kosky, creator of the Millionaire Partner System, holding his ClickFunnels Eight Figure Club award"
                                loading="lazy"
                                className="h-full w-full object-cover object-center"
                            />
                            {/* Elegant gradient anchor for caption */}
                            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink via-ink/85 to-transparent" />
                            <figcaption className="absolute bottom-5 left-6 right-6 flex items-center gap-3 text-cream">
                                <div className="flex items-center justify-center h-9 w-9 rounded-full bg-gold/15 border border-gold/40">
                                    <BadgeCheck className="h-4 w-4 text-gold" strokeWidth={2} />
                                </div>
                                <div className="text-left">
                                    <p className="font-display text-base md:text-lg leading-tight">
                                        Glynn Kosky
                                    </p>
                                    <p className="text-[0.65rem] uppercase tracking-[0.28em] text-cream/70">
                                        Creator &middot; Millionaire Partner System
                                    </p>
                                </div>
                            </figcaption>
                        </figure>
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-7">
                        <Eyebrow>The Official Masterclass &middot; The Millionaire Partner Method&trade;</Eyebrow>
                        <h2
                            data-testid="partner-system-webclass-headline"
                            className="font-display mt-6 text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-cream"
                        >
                            Learn the exact system for building online income &mdash;{" "}
                            <span className="text-gold italic">presented by Glynn Kosky.</span>
                        </h2>

                        <p
                            data-testid="partner-system-webclass-bio"
                            className="mt-7 text-sm md:text-base text-cream/75 leading-relaxed max-w-2xl"
                        >
                            Glynn Kosky has built a strong reputation in the digital
                            marketing world by creating a variety of tools and educational
                            programs aimed at helping online entrepreneurs succeed. The
                            Millionaire Partner System provides a comprehensive,
                            easy-to-follow strategy for making money online &mdash;
                            designed especially for individuals without extensive technical
                            expertise or previous experience.
                        </p>

                        {/* Learning outcomes */}
                        <div className="mt-10">
                            <p className="text-[0.7rem] uppercase tracking-[0.4em] text-gold/85 mb-6">
                                What You&rsquo;ll Learn
                            </p>
                            <ul className="space-y-4" data-testid="partner-system-webclass-outcomes">
                                {WEBCLASS_OUTCOMES.map((outcome, i) => (
                                    <li
                                        key={i}
                                        data-testid={`partner-system-webclass-outcome-${i}`}
                                        className="flex items-start gap-4"
                                    >
                                        <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-gold/15 border border-gold/40 mt-0.5">
                                            <Check
                                                className="h-3.5 w-3.5 text-gold"
                                                strokeWidth={2.5}
                                            />
                                        </span>
                                        <span className="text-sm md:text-[0.95rem] text-cream/85 leading-relaxed">
                                            {outcome}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* CTA */}
                        <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
                            <CtaButton
                                size="lg"
                                label="Access the Free Masterclass"
                                testId="partner-system-webclass-cta"
                            />
                            <span className="text-[0.65rem] uppercase tracking-[0.35em] text-cream/45">
                                No purchase required
                            </span>
                        </div>

                        <p
                            data-testid="partner-system-webclass-source"
                            className="mt-10 text-[0.65rem] text-cream/35 uppercase tracking-[0.25em]"
                        >
                            Content sourced from the official Millionaire Partner System page.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}

// =============================================================
//  §12  GUARANTEE — Official 60-Day Money-Back Guarantee
//  Uses the official refund policy wording verbatim from the
//  Millionaire Partner System source page. No invented terms.
// =============================================================
function Guarantee() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-guarantee"
            className="reveal relative py-24 md:py-32 overflow-hidden"
        >
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-gradient-to-b from-ink-100 via-ink to-ink" />
                <div
                    aria-hidden
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[38rem] w-[38rem] rounded-full bg-gold/8 blur-[130px]"
                />
                <div className="absolute inset-0 bg-noise opacity-25" />
            </div>

            <div className="mx-auto max-w-4xl px-6 md:px-10 lg:px-12 text-center">
                {/* Official Millionaire Partner System logo — brand anchor */}
                <div
                    data-testid="partner-system-guarantee-logo"
                    className="mx-auto mb-14 md:mb-16 w-[180px] md:w-[200px] lg:w-[210px]"
                >
                    <img
                        src={IMG.mpsLogo}
                        alt="Millionaire Partner System official logo"
                        loading="lazy"
                        className="w-full h-auto"
                    />
                </div>

                <div className="mx-auto w-40 md:w-52 lg:w-56" data-testid="partner-system-guarantee-badge">
                    <img
                        src={IMG.guaranteeBadge}
                        alt="Official 100% Money-Back Guarantee badge"
                        loading="lazy"
                        className="w-full h-auto drop-shadow-[0_10px_40px_rgba(212,175,55,0.25)]"
                    />
                </div>

                <Eyebrow className="mt-10 md:mt-12">Official 60-Day Money-Back Guarantee</Eyebrow>
                <h2
                    data-testid="partner-system-guarantee-headline"
                    className="font-display mt-5 text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-cream"
                >
                    A risk-free way to{" "}
                    <span className="text-gold italic">explore the program.</span>
                </h2>

                <p
                    data-testid="partner-system-guarantee-policy"
                    className="mt-8 md:mt-10 text-sm md:text-base text-cream/75 leading-relaxed max-w-2xl mx-auto"
                >
                    If you&rsquo;re not completely satisfied with Millionaire Partner
                    System within 60 days of receiving it, you can request a refund.
                    We will promptly issue a full refund with no questions asked.
                </p>

                <div className="mt-12 flex flex-col items-center gap-4">
                    <CtaButton size="lg" testId="partner-system-guarantee-cta" />
                    <span className="text-[0.65rem] uppercase tracking-[0.35em] text-cream/45">
                        Secure checkout &middot; No commitment required
                    </span>
                </div>

                <p
                    data-testid="partner-system-guarantee-source"
                    className="mt-14 text-[0.65rem] text-cream/35 uppercase tracking-[0.25em]"
                >
                    Refund policy sourced from the official Millionaire Partner System page.
                </p>
            </div>
        </section>
    );
}

// =============================================================
//  §13  FINAL CTA (no image)
// =============================================================
function FinalCta() {
    const ref = useReveal();
    return (
        <section
            ref={ref}
            data-testid="partner-system-final-cta"
            className="reveal relative py-28 md:py-40 overflow-hidden"
        >
            <div className="absolute inset-0 -z-10">
                <div className="absolute inset-0 bg-gradient-to-br from-ink via-ink-100 to-ink" />
                <div
                    aria-hidden
                    className="absolute -top-32 left-1/2 -translate-x-1/2 h-[40rem] w-[40rem] rounded-full bg-gold/10 blur-[120px]"
                />
                <div className="absolute inset-0 bg-noise opacity-30" />
            </div>

            <div className="mx-auto max-w-4xl px-6 md:px-10 text-center">
                <Eyebrow>Getting Started</Eyebrow>
                <h2
                    data-testid="partner-system-final-cta-headline"
                    className="font-display mt-6 text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-cream"
                >
                    A first step,{" "}
                    <span className="text-gold italic">at your own pace.</span>
                </h2>
                <p className="mt-7 md:mt-9 text-sm md:text-base text-cream/75 leading-relaxed max-w-md mx-auto">
                    Free web class. No purchase required.
                </p>
                <div className="mt-11 flex flex-col items-center gap-5">
                    <CtaButton size="lg" testId="partner-system-final-cta-button" />
                </div>

                <div className="mt-20 mx-auto h-px w-40 bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
                <p className="mt-8 text-[0.65rem] text-cream/40 uppercase tracking-[0.35em]">
                    Curated by CrLuys LifeStyle
                </p>
            </div>
        </section>
    );
}

// =============================================================
//  PAGE
// =============================================================
export default function PartnerSystem() {
    React.useEffect(() => {
        document.title =
            "Financial Freedom Changes Everything | Millionaire Partner System | CrLuys LifeStyle";

        const setMeta = (selector, attr, value) => {
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                const [type, name] = selector
                    .replace("meta[", "")
                    .replace("]", "")
                    .split("=");
                el.setAttribute(type, name.replace(/['"]/g, ""));
                document.head.appendChild(el);
            }
            el.setAttribute(attr, value);
        };

        const desc =
            "Education, mentorship, and community for building long-term income opportunities. Access the free introductory web class.";
        const img =
            "https://curated-living-8.emergent.host/social/partner-system-og.jpg";
        const url = "https://curated-living-8.emergent.host/partner-system";
        const title =
            "Financial Freedom Changes Everything | Millionaire Partner System | CrLuys LifeStyle";

        setMeta('meta[name="description"]', "content", desc);
        setMeta('meta[property="og:title"]', "content", title);
        setMeta('meta[property="og:description"]', "content", desc);
        setMeta('meta[property="og:url"]', "content", url);
        setMeta('meta[property="og:image"]', "content", img);
        setMeta('meta[name="twitter:title"]', "content", title);
        setMeta('meta[name="twitter:description"]', "content", desc);
        setMeta('meta[name="twitter:image"]', "content", img);

        let canon = document.querySelector('link[rel="canonical"]');
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", url);

        window.scrollTo(0, 0);
    }, []);

    return (
        <main data-testid="partner-system-page" className="bg-ink text-cream">
            <Hero />
            <HowItWorks />
            <PrivateCommunity />
            <OfficeWorker />
            <ManualWorkers />
            <StrategySession />
            <FamilyFreedom />
            <Lifestyle />
            <Testimonials />
            <Guarantee />
            <WebClass />
            <FinalCta />
        </main>
    );
}
